/**
 * EditTagDialog Component
 * 
 * Диалог для редактирования названия и цвета тега
 * Включает валидацию, предварительный просмотр и произвольный выбор цвета
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ColorPicker } from '@/components/ui/color-picker';
import { TAG_COLORS } from '@/integrations/supabase/types';
import { TagChip } from './TagChip';
import type { Tag } from '@/integrations/supabase/types';
import { useTranslation } from "react-i18next";

interface EditTagDialogProps {
  tag: Tag | null;
  open: boolean;
  onClose: () => void;
  onSave: (tagId: string, data: { name: string; color: string }) => void;
  isSaving?: boolean;
}

export const EditTagDialog: React.FC<EditTagDialogProps> = ({
  tag,
  open,
  onClose,
  onSave,
  isSaving = false
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(TAG_COLORS[0]);
  const [errors, setErrors] = useState<{ name?: string; color?: string }>({});

  // Утилиты для работы с цветами
  const isValidHexColor = (hex: string): boolean => {
    const hexRegex = /^#([0-9A-F]{3}){1,2}$/i;
    return hexRegex.test(hex);
  };

  // Инициализация данных тега при открытии
  useEffect(() => {
    if (tag && open) {
      setName(tag.name);
      setColor(tag.color);
      setErrors({});
    }
  }, [tag, open]);

  const validateForm = () => {
    const newErrors: { name?: string; color?: string } = {};

    // Валидация названия
    if (!name.trim()) {
      newErrors.name = t('records.tag_name_required');
    } else if (name.trim().length < 2) {
      newErrors.name = t('records.tag_name_min_length');
    } else if (name.trim().length > 50) {
      newErrors.name = t('records.tag_name_max_length');
    }

    // Валидация цвета
    if (!color || !isValidHexColor(color)) {
      newErrors.color = t('records.invalid_color');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!tag || !validateForm()) return;

    onSave(tag.id, {
      name: name.trim(),
      color: color
    });
  };

  const handleClose = () => {
    if (isSaving) return;

    setName('');
    setColor(TAG_COLORS[0]);
    setErrors({});
    onClose();
  };

  if (!tag) return null;

  // Создаем preview тег для демонстрации
  const previewTag: Tag = {
    ...tag,
    name: name.trim() || tag.name,
    color: color || tag.color
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-3">
          <DialogTitle className="flex items-center gap-2">
            ✏️ {t('records.edit_tag_title')}
          </DialogTitle>
          <DialogDescription>
            {t('records.edit_tag_desc')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Предварительный просмотр */}
          <div>
            <Label className="text-sm font-medium text-muted-foreground">
              {t('records.preview')}
            </Label>
            <div className="mt-2 p-3 bg-muted/30 rounded-lg border">
              <TagChip tag={previewTag} size="md" />
            </div>
          </div>

          {/* Поле названия */}
          <div className="space-y-2">
            <Label htmlFor="tag-name">{t('records.tag_name_label')}</Label>
            <Input
              id="tag-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors({ ...errors, name: undefined });
                }
              }}
              placeholder={t('records.tag_name_placeholder')}
              disabled={isSaving}
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          {/* Выбор цвета */}
          <div className="space-y-3">
            <Label>{t('records.tag_color_label')}</Label>
            <div className="space-y-3">
              {/* Основной color picker */}
              <ColorPicker
                value={color}
                onChange={(newColor) => {
                  setColor(newColor);
                  if (errors.color) {
                    setErrors({ ...errors, color: undefined });
                  }
                }}
                disabled={isSaving}
                presetColors={[...TAG_COLORS]}
                showFormatTabs={true}
                showTextInput={true}
              />

              {/* Отображение ошибки */}
              {errors.color && (
                <p className="text-sm text-destructive">{errors.color}</p>
              )}
            </div>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {(() => {
              const hasNameChange = name.trim() !== tag.name;
              const hasColorChange = color !== tag.color;

              if (hasNameChange || hasColorChange) {
                return (
                  <span className="text-amber-600 flex items-center gap-1">
                    <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                    {t('records.unsaved_changes')}
                  </span>
                );
              }
              return <span className="text-muted-foreground">{t('records.no_changes')}</span>;
            })()}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSaving}
              size="sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={(() => {
                if (isSaving) return true;
                const hasNameChange = name.trim() !== tag.name;
                const hasColorChange = color !== tag.color;
                return !hasNameChange && !hasColorChange;
              })()}
              className="min-w-[100px]"
              size="sm"
            >
              {isSaving ? t('records.saving') : t('records.save')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};